import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getPrivateAxiosInstance } from '../../../axiosInstances/axiosPrivate';
import { AcademiaResponse } from '../../../types/common';
import { Config, ConfigReqBody } from '../../../types/configurations';
import { returnQueryConfig } from '../../../utils/commonFunctions';

const useConfigActions = (
  whichAction: 'configList' | '',
  associateId?: number
) => {
  // query function to get configuration list from database declared here
  const getConfigListHandler = useQuery({
    queryKey: ['config-list'],
    queryFn: async (): Promise<AcademiaResponse<Array<Config>>> => {
      const response = await getPrivateAxiosInstance('appointment').get(
        '/service/config/list/'
      );

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'configList'),
  });

  // query function to fetch all the service configs by associate id declared here
  const getServiceConfigsByIdHandler = useQuery({
    queryKey: ['service-configs-by-id', associateId],
    queryFn: async (): Promise<AcademiaResponse<Array<Config>>> => {
      const response = await getPrivateAxiosInstance('appointment').get(
        `/associate/specific/service/configs/${associateId}/`
      );

      return response.data;
    },

    ...returnQueryConfig(!!associateId),
  });

  // mutation function to create new config declared here
  const createNewConfigHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: ConfigReqBody;
    }): Promise<AcademiaResponse<Config>> => {
      const response = await getPrivateAxiosInstance('appointment').post(
        '/service/config/create/',
        body
      );

      return response.data;
    },
  });

  // mutation function to update config declared here
  const updateConfigHandler = useMutation({
    mutationFn: async ({
      configId,
      body,
    }: {
      configId: number;
      body: ConfigReqBody;
    }): Promise<AcademiaResponse<Config>> => {
      const response = await getPrivateAxiosInstance('appointment').patch(
        `/service/config/${configId}/partial/update/`,
        body
      );

      return response.data;
    },
  });

  // mutation function to delete config by id declared here
  const deleteConfigByIdHandler = useMutation({
    mutationFn: ({
      configId,
    }: {
      configId: number;
    }): Promise<AxiosResponse<AcademiaResponse<null>>> =>
      getPrivateAxiosInstance('appointment').delete(
        `/service/config/${configId}/delete/`
      ),
  });

  return {
    getConfigListHandler,
    getServiceConfigsByIdHandler,
    createNewConfigHandler,
    updateConfigHandler,
    deleteConfigByIdHandler,
  };
};

export default useConfigActions;
